import React from "react";
import {
  Container,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import SectionHeading from "./SectionHeading";
import { colors, fonts } from "../theme";

const rows = [
  {
    axis: "Core Approach",
    training: "Short-term, isolated training modules focused on rote memorization.",
    project: "Transactional delivery of generic, pre-built projects.",
    crescent: "Long-term integration focused on sustainable academic excellence.",
  },
  {
    axis: "Student Development",
    training: "Basic attendance certificates with limited practical coding skills.",
    project: "Superficial understanding of copied codebases.",
    crescent: "Verifiable technical portfolios and strong industry readiness.",
  },
  {
    axis: "Innovation Focus",
    training: "Zero focus on creative thinking or technical problem solving.",
    project: "Mainly focused on passing basic academic project criteria.",
    crescent: "Fosters an active culture of original research and patent creation.",
  },
  {
    axis: "Research Awareness",
    training: "Provides no support for faculty research or publications.",
    project: "Completely isolated from academic research methodologies.",
    crescent: "Directly assists with patent drafts and high-quality research manuscripts.",
  },
  {
    axis: "Faculty Enablement",
    training: "No options for deep faculty upskilling or cloud tools instruction.",
    project: "Completely detached from faculty professional development grids.",
    crescent: "Provides continuous technical training and project guidance for educators.",
  },
  {
    axis: "Long-Term Outcomes",
    training: "Temporary spikes in basic placement indicators with zero systemic residue.",
    project: "High risks of plagiarism flags during regulatory evaluation cycles.",
    crescent: "Consistent improvements in ranking metrics, placements and patents.",
  },
  {
    axis: "Partnership Model",
    training: "Purely transactional, short-term vendor relationships.",
    project: "Unstructured, seasonal client-vendor interactions.",
    crescent: "Strategic, year-round collaboration aligned with institutional goals.",
  },
];

const headCellSx = {
  color: colors.white,
  fontFamily: fonts.heading,
  fontWeight: 700,
  fontSize: "0.9rem",
  borderBottom: "none",
  verticalAlign: "top",
};

const bodyCellSx = {
  color: colors.textBody,
  fontSize: "0.88rem",
  lineHeight: 1.55,
  verticalAlign: "top",
  borderBottom: "1px solid #eef2f7",
};

const Comparison = () => {
  return (
    <section id="comparison-section" className="comparison-section">
      <Box sx={{ py: { xs: 5, md: 7 } }}>
        <Container>
          <SectionHeading
            eyebrow="Why Institutions Partner With Crescent EdTech"
            title="A Partnership Model — Not a Transactional Vendor"
            subtitle="How Crescent EdTech's long-term partnership differs from traditional training vendors and project centres across every axis that matters."
          />
          <Box
            className="table-scroll"
            sx={{ borderRadius: 3, border: "1px solid #e6edf7", overflowX: "auto" }}
          >
            <TableContainer>
              <Table sx={{ minWidth: 820 }}>
                <TableHead>
                  <TableRow sx={{ background: colors.navy }}>
                    <TableCell sx={{ ...headCellSx }}>Evaluation Axis</TableCell>
                    <TableCell sx={{ ...headCellSx }}>Traditional Training Provider</TableCell>
                    <TableCell sx={{ ...headCellSx }}>Traditional Project Centre</TableCell>
                    <TableCell
                      sx={{
                        ...headCellSx,
                        background: colors.brandGradient,
                      }}
                    >
                      Crescent EdTech Partnership
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((r, i) => (
                    <TableRow
                      key={r.axis}
                      sx={{
                        bgcolor: i % 2 ? "#f8fafd" : colors.white,
                        transition: "background-color .2s ease",
                        "&:hover": { bgcolor: "rgba(56,182,255,0.08)" },
                      }}
                    >
                      <TableCell sx={{ ...bodyCellSx, fontWeight: 700, color: colors.navy }}>
                        {r.axis}
                      </TableCell>
                      <TableCell sx={{ ...bodyCellSx, color: colors.textMuted }}>{r.training}</TableCell>
                      <TableCell sx={{ ...bodyCellSx, color: colors.textMuted }}>{r.project}</TableCell>
                      <TableCell
                        sx={{
                          ...bodyCellSx,
                          color: colors.navy,
                          fontWeight: 600,
                          bgcolor: "rgba(56,182,255,0.06)",
                          borderLeft: `2px solid ${colors.blue}`,
                        }}
                      >
                        {r.crescent}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Container>
      </Box>
    </section>
  );
};

export default Comparison;
